/*───────────────────────────────────────────────────────────────*
 *  MatchingTool.jsx — v4.0
 *     • considers ALL open mentors via chunking
 *     • popularity penalty (busy mentors fed later)
 *     • editable constants: REFRESH_LOCK_SEC, CHUNK_SIZE
 *     • retains countdown button, Last-Update column, etc.
 *───────────────────────────────────────────────────────────────*/

import React, { useEffect, useState, useRef } from 'react';
import { getRecords, updateRecord } from './api/airtable';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';

/*── Editable constants ────────────────────────────────────────*/
const REFRESH_LOCK_SEC = 30;   // button lock duration
const CHUNK_SIZE       = 30;   // mentors per LLM prompt
/*──────────────────────────────────────────────────────────────*/

const MODEL     = 'llama-3.1-8b-instant';
const BASE_URL  = 'https://api.groq.com/openai/v1';
const TOKEN_ENV = 'REACT_APP_GROQ_API_KEY';

const BULLET_FIELD       = 'Bullet Summary for AI Matching';
const BULLET_DATE_FIELD  = 'Bullet Summary Date';
const INFO_FIELD         = 'Info About Matches';

/*────────────────────────────────────────────────────*
  Util – safe JSON extraction
*────────────────────────────────────────────────────*/
function extractJSON(str) {
  if (!str) return null;
  const fenced = str.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenced) str = fenced[1].trim();
  const arrIdx = str.indexOf('[');
  const objIdx = str.indexOf('{');
  if (arrIdx !== -1) str = str.slice(arrIdx, str.lastIndexOf(']') + 1);
  else if (objIdx !== -1) str = str.slice(objIdx, str.lastIndexOf('}') + 1);
  try { return JSON.parse(str); } catch { return null; }
}

/*────────────────────────────────────────────────────*
  Helper – single 2-sentence summary
*────────────────────────────────────────────────────*/
async function generateSummary(fields, token) {
  const prompt = [
    'Create a concise TWO-SENTENCE description of this person (background, studies, experiences, heritage, interests).',
    'Avoid listing field names; write natural prose. Respond with **only** those two sentences.\n\n',
    'JSON:\n', JSON.stringify(fields), '\n\nSummary:',
  ].join('');

  const res = await fetch(`${BASE_URL}/chat/completions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      model: MODEL,
      max_tokens: 120,
      temperature: 0.4,
      messages: [{ role: 'user', content: prompt }],
    }),
  });

  if (!res.ok) throw new Error(await res.text());
  const txt = (await res.json())?.choices?.[0]?.message?.content?.trim() || '';
  return txt.split(/\n+/)[0];
}

/*────────────────────────────────────────────────────*
  Helper – batch create mentor summaries
*────────────────────────────────────────────────────*/
const MENTOR_SUMMARY_FIELDS = [
  'Undergraduate Major Grouping',
  'Labels for Heritage',
  'Combined labels for experiences 1 & 2',
  'Labels for Community',
  'Specialty / Discipline',
  'Areas of Interest',
];

const slim = r =>
  Object.fromEntries(
    MENTOR_SUMMARY_FIELDS.map(k => [k, r[k]]).filter(([, v]) => v && v.length)
  );

async function buildMentorSummaries(mentors, token, todayISO) {
  const missing = mentors.filter(m => !m.fields[BULLET_FIELD]);
  if (!missing.length) return;

  const BATCH = 5;
  for (let i = 0; i < missing.length; i += BATCH) {
    const chunk  = missing.slice(i, i + BATCH);
    const prompt = [
      'For every mentor below write a concise TWO-SENTENCE summary.',
      'Return **only** valid JSON of the form {"Full Name":"summary", …} with no extra text.\n\n',
      JSON.stringify(chunk.map(m => ({ name: m.fields['Full Name'], data: slim(m.fields) }))),
    ].join('');

    const res = await fetch(`${BASE_URL}/chat/completions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        model: MODEL,
        max_tokens: 250,
        temperature: 0.4,
        messages: [{ role: 'user', content: prompt }],
      }),
    });

    if (!res.ok) throw new Error(await res.text());
    const content   = (await res.json())?.choices?.[0]?.message?.content || '{}';
    const summaries = extractJSON(content) || {};

    chunk.forEach(m => {
      const s = summaries[m.fields['Full Name']];
      if (s) {
        m.fields[BULLET_FIELD] = s;
        updateRecord(m.id, {
          [BULLET_FIELD]: s,
          [BULLET_DATE_FIELD]: todayISO,
        }).catch(console.error);
      }
    });
  }
}

/*────────────────────────────────────────────────────*/
export default function MatchingTool() {
  return (
    <div className="p-8 space-y-12">
      <AllMatchTable />
      <SimilarityGraph />
    </div>
  );
}

/*────────────────────────────────────────────────────*/
function AllMatchTable() {
  /*──────── state ───────*/
  const [records, setRecords]     = useState([]);
  const [mentors, setMentors]     = useState([]);
  const [recommendations, setRec] = useState({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [updating, setUpdating]       = useState(false);
  const [progress, setProgress]       = useState({ done: 0, total: 0 });

  const [refreshingId, setRefreshingId] = useState(null);
  const [countdown,    setCountdown]    = useState(0);
  const [rateLimitMsg, setRateLimitMsg] = useState(null);

  const [tooltip, setTooltip]   = useState({ visible: false, x: 0, y: 0, text: '' });
  const tableRef                = useRef(null);
  const navigate                = useNavigate();

  /*──────── data load ───*/
  useEffect(() => {
    (async () => {
      try {
        const mentees = await getRecords();
        const mtrs    = await getRecords({ table: 'Mentors', filterByFormula: "{Open to Mentoring}='Yes'" });
        setRecords(mentees);
        setMentors(mtrs);
        checkNeedUpdate([...mentees, ...mtrs]);
      } catch (e) { console.error(e); setError('Failed to load records'); }
      finally { setLoading(false); }
    })();
  }, []);

  const checkNeedUpdate = people => {
    const now = Date.now();
    setNeedsUpdate(
      people.some(p => {
        const date = p.fields[BULLET_DATE_FIELD];
        const age  = date ? (now - new Date(date).getTime()) / 86_400_000 : Infinity;
        return !p.fields[BULLET_FIELD] || age > 60;
      })
    );
  };

  /*──────── tooltip helpers ─*/
  const showTooltip = (e, text) => {
    const rect = tableRef.current.getBoundingClientRect();
    setTooltip({ visible: true, x: e.clientX - rect.left + 10, y: e.clientY - rect.top + 10, text });
  };
  const hideTooltip = () => setTooltip(t => ({ ...t, visible: false }));

  /*──────── updater (unchanged) ─*/
  async function runUpdater() {
    const token = process.env[TOKEN_ENV];
    if (!token) { setError('Missing Groq API key'); return; }

    const queue = [...records].filter(p => {
      const date = p.fields[BULLET_DATE_FIELD];
      const age  = date ? (Date.now() - new Date(date).getTime()) / 86_400_000 : Infinity;
      return !p.fields[BULLET_FIELD] || age > 60;
    });

    if (!queue.length) { setNeedsUpdate(false); return; }

    setUpdating(true);
    setProgress({ done: 0, total: queue.length });

    for (const person of queue) {
      try {
        const s = await generateSummary(person.fields, token);
        await updateRecord(person.id, {
          [BULLET_FIELD]: s,
          [BULLET_DATE_FIELD]: new Date().toISOString().slice(0, 10),
        });
        person.fields[BULLET_FIELD] = s;
      } catch (e) { console.error(e); }

      setProgress(prev => ({ ...prev, done: prev.done + 1 }));
      await new Promise(r => setTimeout(r, 15_000));   // throttle
    }

    setUpdating(false);
    setNeedsUpdate(false);
  }

  /*──────── refresh handler – NEW logic ─*/
  const handleRefreshRecommendations = async recId => {
    if (needsUpdate || updating || refreshingId) return;

    const token = process.env[TOKEN_ENV];
    if (!token) { setError('Missing Groq API key'); return; }

    const mentee = records.find(r => r.id === recId);
    if (!mentee) return;

    /* lock UI */
    setRefreshingId(recId);
    setCountdown(REFRESH_LOCK_SEC);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev === 1) clearInterval(timer);
        return prev - 1;
      });
    }, 1000);

    const todayISO = new Date().toISOString().slice(0, 10);

    /* ensure mentee summary */
    if (!mentee.fields[BULLET_FIELD] ||
        ((Date.now() - new Date(mentee.fields[BULLET_DATE_FIELD] || 0)) / 86_400_000) > 60) {
      try {
        const s = await generateSummary(mentee.fields, token);
        await updateRecord(mentee.id, { [BULLET_FIELD]: s, [BULLET_DATE_FIELD]: todayISO });
        mentee.fields[BULLET_FIELD] = s;
      } catch (e) { console.error(e); }
    }

    /*──────── popularity counts ─────*/
    const popCount = {};
    mentors.forEach(m => { popCount[m.fields['Full Name']] = 0; });
    records.forEach(r => {
      ['First','Second','Third'].forEach(ord => {
        const name = r.fields[`Name of ${ord} Mentor Recommendation`];
        if (name && popCount[name] !== undefined) popCount[name] += 1;
      });
    });

    /* sorted mentor pool (low-load first) */
    const mentorPool = mentors
      .filter(m => m.fields['Full Name'] !== mentee.fields['Full Name'])
      .sort((a, b) => (popCount[a.fields['Full Name']] ?? 0) - (popCount[b.fields['Full Name']] ?? 0));

    /* ensure everyone has bullet summaries */
    try { await buildMentorSummaries(mentorPool, token, todayISO); }
    catch (e) { console.error(e); }

    /*──────── chunk-by-chunk matching ────*/
    const chosen = new Map();   // mentor → rationale
    for (let i = 0; i < mentorPool.length && chosen.size < 3; i += CHUNK_SIZE) {
      const chunk = mentorPool.slice(i, i + CHUNK_SIZE);

      const mentorLines = chunk.map(m => {
        const load = popCount[m.fields['Full Name']];
        return `- ${m.fields['Full Name']} (currently mentoring ${load} students): ${m.fields[BULLET_FIELD]}`;
      });

      const prompt = [
        'You are a mentorship-matching assistant.',
        'Below is a mentee summary and a list of potential mentors.',
        'Pick **up to three** mentors that align well AND have the lightest current load.',
        'Prefer mentors with a lower "(currently mentoring X students)" number.',
        'Return strictly valid JSON like:',
        '[{"name":"Mentor A","rationale":"…"}] (maximum 3 items).',
        'Do NOT repeat mentors already recommended to this mentee.\n\n',
        `MENTEE:\n${mentee.fields[BULLET_FIELD]}\n\nMENTORS:\n${mentorLines.join('\n')}`,
      ].join('\n');

      let resp;
      try {
        resp = await fetch(`${BASE_URL}/chat/completions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            model: MODEL,
            temperature: 0.2,
            max_tokens: 320,
            messages: [{ role: 'user', content: prompt }],
          }),
        });
      } catch (e) { console.error(e); }

      if (!resp?.ok) { console.error('Groq error', await resp?.text()); continue; }

      const raw  = (await resp.json())?.choices?.[0]?.message?.content || '';
      (extractJSON(raw) || []).forEach(r => {
        if (r?.name && !chosen.has(r.name) && chosen.size < 3 &&
            r.name !== mentee.fields['Full Name']) {
          chosen.set(r.name, r.rationale || '');
        }
      });
    }

    /* fallback fill if <3 */
    if (chosen.size < 3) {
      mentorPool.forEach(m => {
        if (chosen.size < 3 && !chosen.has(m.fields['Full Name']))
          chosen.set(m.fields['Full Name'], 'Selected to balance mentor workload.');
      });
    }

    const finalRecs = [...chosen.entries()].slice(0, 3).map(([name, rationale]) => ({ name, rationale }));

    /* UI + Airtable writeback */
    setRec(prev => ({ ...prev, [recId]: finalRecs }));

    const updates = finalRecs.reduce((acc, it, idx) => {
      const n = ['First','Second','Third'][idx];
      acc[`Name of ${n} Mentor Recommendation`] = it.name;
      acc[`Rationale for ${n} Mentor`]         = it.rationale;
      return acc;
    }, {});
    updates[INFO_FIELD] = `Auto-match ${todayISO} via ${MODEL}`;

    try { await updateRecord(recId, updates); } catch (e) { console.error(e); }
  };

  /* unlock when countdown finishes */
  useEffect(() => { if (countdown <= 0 && refreshingId) setRefreshingId(null); }, [countdown, refreshingId]);

  /*──────── helper for Last-Update column ─*/
  const parseDate = txt => (txt || '').match(/\d{4}-\d{2}-\d{2}/)?.[0] || '—';

  /*──────── UI render ─────*/
  if (loading) return <p className="text-center mt-8">Loading…</p>;
  if (error)   return <p className="text-center mt-8 text-red-500">{error}</p>;

  const anyDisabled = Boolean(refreshingId);
  const estMin      = Math.ceil((progress.total - progress.done) * 0.25);

  const sorted = [...records].sort((a, b) =>
    (a.fields['Full Name'] || '').split(' ').pop()
      .localeCompare((b.fields['Full Name'] || '').split(' ').pop())
  );
  const allPeople   = [...records, ...mentors];
  const lookupByName = n => allPeople.find(p => p.fields['Full Name'] === n);

  return (
    <>
      {/*──────── rate-limit modal ─────*/}
      {rateLimitMsg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm text-center space-y-4">
            <p className="text-gray-800">{rateLimitMsg}</p>
            <button
              onClick={() => setRateLimitMsg(null)}
              className="px-4 py-2 bg-[#8C1515] text-white rounded hover:bg-[#691010]"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/*──────── stale-summary banner ─*/}
      {needsUpdate && !updating && (
        <div className="mb-4 p-4 bg-yellow-100 border border-yellow-300 rounded">
          <p className="mb-2 text-sm text-yellow-900">
            You must update bullet summaries before the AI model can give recommendations.
          </p>
          <button
            onClick={runUpdater}
            disabled={anyDisabled}
            className={`px-4 py-1 rounded text-white
                        ${anyDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#8C1515] hover:bg-[#691010]'}`}
          >
            Update
          </button>
        </div>
      )}

      {/*──────── update progress banner ─*/}
      {updating && (
        <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded">
          <p className="text-sm text-indigo-900">
            Updating summaries … {progress.done}/{progress.total} (≈ {estMin} min left)
          </p>
        </div>
      )}

      {/*──────── Match table ─────*/}
      <div ref={tableRef} className="bg-white shadow rounded-lg p-6 relative">
        <h2 className="text-2xl font-bold text-[#8C1515] mb-4">All Match Table</h2>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Student or Alumni','Match 1','Match 2','Match 3','Last Update','Actions']
                  .map(col => (
                    <th key={col} className="px-4 py-2 text-sm font-semibold text-gray-700">{col}</th>
                  ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sorted.map((rec, i) => {
                const f          = rec.fields;
                const recs       = recommendations[rec.id];
                const inProgress = refreshingId === rec.id;
                const lastDate   = parseDate(f[INFO_FIELD]);

                return (
                  <tr key={rec.id}
                      className={i % 2 ? 'bg-gray-50 hover:bg-gray-100' : 'bg-white hover:bg-gray-100'}>
                    {/* mentee */}
                    <td className="px-4 py-3 text-sm text-gray-800">
                      {f['Full Name'] ? (
                        <span
                          onClick={() => navigate(`/details/${rec.id}`)}
                          className="cursor-pointer text-[#1D4ED8] hover:underline"
                        >
                          {f['Full Name']}
                        </span>
                      ) : '—'}
                    </td>

                    {/* mentor cells */}
                    {['First','Second','Third'].map((ord, idx) => {
                      const name      = recs ? recs[idx]?.name      : f[`Name of ${ord} Mentor Recommendation`];
                      const rationale = recs ? recs[idx]?.rationale : f[`Rationale for ${ord} Mentor`] || 'No rationale.';
                      const person    = lookupByName(name);
                      return (
                        <td key={ord} className="px-4 py-3 text-sm text-gray-800">
                          {name ? (
                            <span
                              onClick={() => person && navigate(`/details/${person.id}`)}
                              onMouseEnter={e => showTooltip(e, rationale)}
                              onMouseLeave={hideTooltip}
                              className="cursor-pointer text-[#1D4ED8] hover:underline"
                            >
                              {name}
                            </span>
                          ) : '—'}
                        </td>
                      );
                    })}

                    {/* last update */}
                    <td className="px-4 py-3 text-sm text-gray-800">{lastDate}</td>

                    {/* action button */}
                    <td className="px-4 py-3 text-sm text-gray-800">
                      <button
                        onClick={() => handleRefreshRecommendations(rec.id)}
                        disabled={anyDisabled}
                        className={`
                          relative w-56 px-2 py-2 rounded text-white tracking-tight
                          ${inProgress
                            ? 'bg-[#6F0F0F] cursor-wait'
                            : anyDisabled
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-[#8C1515] hover:bg-[#691010]'}
                        `}
                      >
                        {inProgress ? 'Updating…' : 'Refresh Mentor Recommendations'}
                        {inProgress && (
                          <>
                            {/* seconds */}
                            <span className="absolute top-1 right-2 text-xs opacity-80">
                              {countdown}s
                            </span>
                            {/* progress bar */}
                            <span
                              className="absolute left-0 bottom-0 h-[3px] bg-white/80 rounded-r"
                              style={{ width: `${((REFRESH_LOCK_SEC - countdown) / REFRESH_LOCK_SEC) * 100}%` }}
                            />
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* tooltip */}
        {tooltip.visible && (
          <div
            className="absolute z-10 w-max max-w-xs rounded bg-gray-800 p-2 text-xs text-white shadow-lg"
            style={{ top: tooltip.y, left: tooltip.x }}
          >
            {tooltip.text}
          </div>
        )}
      </div>
    </>
  );
}




/*─────────────────────────────────────────────*
 *  SimilarityGraph (unchanged from earlier)
 *─────────────────────────────────────────────*/
function SimilarityGraph() {
  const svgRef     = useRef(null);
  const tooltipRef = useRef(null);
  const legendRef  = useRef(null);

  useEffect(() => {
    (async () => {
      const records = await getRecords();

      /* build nodes & links */
      const nodes = records.map(r => ({
        id: r.fields['Full Name'],
        group: Array.isArray(r.fields['Undergraduate Major Grouping'])
          ? r.fields['Undergraduate Major Grouping'][0]
          : r.fields['Undergraduate Major Grouping'] || 'Unknown',
      }));

      const labelFields = [
        'Combined labels for experiences 1 & 2',
        'Labels for Heritage',
        'Labels for Motivation for Workshop',
        'Labels for Community',
      ];

      const links = [];
      records.forEach((r1, i) =>
        records.slice(i + 1).forEach(r2 => {
          let score = 0;
          const val = f => (Array.isArray(f) ? f[0] : f);

          if (val(r1.fields['Undergraduate Major Grouping']) === val(r2.fields['Undergraduate Major Grouping']))
            score += 1;
          if (r1.fields['Current Undergraduate Institution Type'] === r2.fields['Current Undergraduate Institution Type'])
            score += 0.7;

          labelFields.forEach(fld => {
            const a1 = r1.fields[fld] || [],
                  a2 = r2.fields[fld] || [];
            if (Array.isArray(a1) && Array.isArray(a2))
              score += 0.5 * a1.filter(x => a2.includes(x)).length;
          });

          if (score > 0) links.push({ source: r1.fields['Full Name'], target: r2.fields['Full Name'], value: score });
        })
      );

      /* d3 setup */
      const svg = d3.select(svgRef.current);
      svg.selectAll('*').remove();
      d3.select(legendRef.current).selectAll('*').remove();

      const width  = window.innerWidth;
      const height = 600;
      svg.attr('width', width).attr('height', height);

      const g = svg.append('g');
      svg.call(d3.zoom().scaleExtent([0.5, 5]).on('zoom', e => g.attr('transform', e.transform)));

      const groups = Array.from(new Set(nodes.map(d => d.group))).sort();
      const color  = d3.scaleOrdinal(d3.schemeCategory10).domain(groups);

      groups.forEach(grp =>
        d3.select(legendRef.current)
          .append('div')
          .html(`<span style="color:${color(grp)}">⬤</span> ${grp}`)
          .style('font-size', '12px')
          .style('margin-bottom', '4px')
      );

      const linkForce = d3.forceLink(links).id(d => d.id).distance(120).strength(d => Math.min(1, d.value));

      const simulation = d3.forceSimulation(nodes)
        .force('link', linkForce)
        .force('charge', d3.forceManyBody().strength(-200))
        .force('collide', d3.forceCollide(15))
        .force('center', d3.forceCenter(width / 2, height / 2))
        .force('x', d3.forceX(d => (width / (groups.length + 1)) * (groups.indexOf(d.group) + 1)).strength(0.8))
        .force('y', d3.forceY(height / 2).strength(0.2));

      const tooltip = d3.select(tooltipRef.current)
        .style('position', 'fixed')
        .style('pointer-events', 'none')
        .style('background', 'rgba(0,0,0,0.7)')
        .style('color', '#fff')
        .style('padding', '6px 8px')
        .style('border-radius', '4px')
        .style('font-size', '12px')
        .style('display', 'none');

      g.append('g')
        .selectAll('circle')
        .data(nodes)
        .join('circle')
        .attr('r', 6)
        .attr('fill', d => color(d.group))
        .call(
          d3.drag()
            .on('start', (e, d) => {
              if (!e.active) simulation.alphaTarget(0.3).restart();
              d.fx = d.x; d.fy = d.y;
            })
            .on('drag', (e, d) => { d.fx = e.x; d.fy = e.y; })
            .on('end',  (e, d) => {
              if (!e.active) simulation.alphaTarget(0);
              d.fx = null; d.fy = null;
            })
        )
        .on('mouseover', (e, d) => {
          tooltip.html(d.id).style('display', 'block')
            .style('left', `${e.clientX + 10}px`).style('top', `${e.clientY + 10}px`);
        })
        .on('mouseout', () => tooltip.style('display', 'none'));

      simulation.on('tick', () =>
        g.selectAll('circle').attr('cx', d => d.x).attr('cy', d => d.y)
      );
    })();
  }, []);

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-2xl font-bold text-[#8C1515] mb-4">Similarity Graph</h2>
      <p className="mb-4 italic text-gray-700">
        Each dot represents a student, coloured by undergraduate major grouping. Nodes cluster by shared major,
        institution type, and common labels. Zoom/pan to explore, hover for names.
      </p>

      <div className="relative w-full h-[600px]">
        <svg ref={svgRef} className="w-full h-full bg-gray-100 rounded" />
        <div ref={legendRef}  className="absolute top-4 left-4 bg-white p-2 rounded border text-gray-700 shadow" />
        <div ref={tooltipRef} className="tooltip" />
      </div>
    </div>
  );
}
